// 공용 첨부 보기 창 (2026-10-01): 영업 · 경영 · 캘린더 · 전자결재 · 인사 · 업무 평가의 첨부 링크
// a[data-file-view] 를 누르면 내려받는 대신 #fileViewer 에서 연다. 바이트를 받아 공용 미리보기
// (file-preview.js)가 그리고(글자 · 표 · PDF · 한글 · Word · PowerPoint · zip · 그림 · 동영상 · 소리),
// 그리지 못하는 형식은 파일 카드와 「내려받기」.
//
// 링크가 쓰는 속성 (data-name 말고는 전부 골라 쓴다):
//   data-name · data-size · data-date · data-project — 머리의 이름과 흐린 메타 줄
//   data-file-key   — 문서 전체에서 같은 키의 첨부를 ‹ › 로 오간다(업무 평가: 한 기록의 띠 · 본문 그림 ·
//                      결과물 칩이 서로 다른 상자에 있다). 없으면 가장 가까운 [data-file-group] 안의 첨부.
//   data-download   — 「내려받기」 주소(없으면 링크 주소)
//   data-open-url   — 원본 시스템에서 열기 ↗ (글자는 가장 가까운 [data-file-open-label], 없으면 창의 기본 문구)
//   data-file-msg   — 실패 답의 JSON message 가 읽는 이의 문장이다(업무 평가 중계) — 없으면 창의 한 문장
//
// 창은 body 수준(index.html) 한 벌이라 SPA 교체에 살아남고, 링크는 문서 위임으로 받는다 — 캘린더 패널 ·
// 결재 문서 모달 · 영업/인사 패널처럼 나중에 그려지는 목록도 그대로 된다. 미리보기 요청에는 ?view=1 을
// 붙인다: 인사 첨부는 그것을 「열람」 감사로 남기고(내려받기와 따로), 다른 경로는 무시한다.

let previewModule = null;
const loadPreview = () => (previewModule ||= import('./file-preview.js'));

const withParam = (href, param) => `${href}${href.includes('?') ? '&' : '?'}${param}`;

export function setupFileViewer() {
    const overlay = document.getElementById('fileViewer');
    if (!overlay || overlay.dataset.bound) return;
    overlay.dataset.bound = '1';
    const byId = (id) => document.getElementById(id);
    const nameEl = byId('fileViewerName');
    const metaEl = byId('fileViewerMeta');
    const countEl = byId('fileViewerCount');
    const stage = byId('fileViewerStage');
    const prevBtn = byId('fileViewerPrev');
    const nextBtn = byId('fileViewerNext');
    const downloadLink = byId('fileViewerDownload');
    const openLink = byId('fileViewerOpen');
    const closeBtn = byId('fileViewerClose');
    const labels = overlay.dataset;
    let list = [];
    let index = 0;
    let restoreTo = null;
    let preview = null;
    let seq = 0;

    const isOpen = () => overlay.classList.contains('show');
    const reset = () => {
        seq += 1;
        if (preview) {
            preview.destroy();
            preview = null;
        }
        delete stage.dataset.loading;
        delete stage.dataset.fill;
        stage.replaceChildren();
    };
    // 같은 키 · 같은 목록의 첨부를 화면에 나온 순서로, 같은 주소는 한 번만 (캘린더 파일 칸은 썸네일과 이름이,
    // 업무 평가는 띠의 썸네일과 본문 그림이 같은 주소다).
    const groupOf = (link) => {
        const key = link.dataset.fileKey;
        let links;
        if (key) {
            links = [...document.querySelectorAll('a[data-file-view][data-file-key]')].filter((a) => a.dataset.fileKey === key);
        } else {
            const scope = link.closest('[data-file-group]');
            if (!scope) return [link];
            links = [...scope.querySelectorAll('a[data-file-view]')];
        }
        const seen = new Set();
        const out = [];
        links.forEach((a) => {
            const href = a.getAttribute('href');
            if (!href || seen.has(href) || overlay.contains(a)) return;
            seen.add(href);
            out.push(a);
        });
        return out.length ? out : [link];
    };
    const nameOf = (a) => (a.dataset.name || a.textContent || '').trim();
    const showError = (text) => {
        delete stage.dataset.fill;
        const p = document.createElement('p');
        p.className = 'file-viewer-error';
        p.textContent = text || labels.lError || '';
        stage.replaceChildren(p);
    };
    const showCard = (a) => {
        delete stage.dataset.fill;
        const box = document.createElement('div');
        box.className = 'file-viewer-card';
        const name = document.createElement('strong');
        name.textContent = nameOf(a);
        const hint = document.createElement('p');
        hint.textContent = labels.lNoPreview || '';
        box.append(name, hint);
        stage.replaceChildren(box);
    };
    const render = async () => {
        reset();
        const mine = seq;
        const a = list[index];
        if (!a) return;
        const href = a.getAttribute('href');
        const name = nameOf(a);
        nameEl.textContent = name;
        metaEl.textContent = [a.dataset.size, a.dataset.date, a.dataset.project].filter(Boolean).join(' · ');
        countEl.textContent = list.length > 1 ? `${index + 1} / ${list.length}` : '';
        prevBtn.hidden = nextBtn.hidden = list.length < 2;
        prevBtn.disabled = index === 0;
        nextBtn.disabled = index === list.length - 1;
        downloadLink.href = a.dataset.download || href;
        downloadLink.setAttribute('download', name);
        const openURL = a.dataset.openUrl || '';
        if (openURL) {
            openLink.href = openURL;
            openLink.textContent = `${a.closest('[data-file-open-label]')?.dataset.fileOpenLabel || labels.lOpen || ''} ↗`;
            openLink.hidden = false;
        } else {
            openLink.removeAttribute('href');
            openLink.hidden = true;
        }
        const { renderPreview, previewKind, PreviewError } = await loadPreview();
        if (mine !== seq) return;
        if (!previewKind(name)) {
            showCard(a);
            return;
        }
        stage.dataset.loading = '1';
        stage.dataset.fill = '1';
        try {
            const res = await fetch(withParam(href, 'view=1'), { credentials: 'same-origin' });
            if (mine !== seq) return;
            // 모듈마다 오류 본문이 다르다(번역 키 · 영어 문장 · 빈 본문) — 창은 한 문장으로 말하고,
            // 읽는 이의 문장을 돌려주는 중계(data-file-msg)만 그 문장을 쓴다.
            if (!res.ok || res.redirected) {
                let text = labels.lError;
                if (!res.redirected && 'fileMsg' in a.dataset) {
                    try { text = (await res.json()).message || text; } catch { /* not JSON */ }
                }
                if (mine === seq) showError(text);
                return;
            }
            const bytes = new Uint8Array(await res.arrayBuffer());
            if (mine !== seq) return;
            const handle = await renderPreview(stage, { bytes, name, labels, onKey });
            if (mine !== seq) {
                handle.destroy();
                return;
            }
            preview = handle;
        } catch (err) {
            if (mine !== seq) return;
            if (err instanceof PreviewError) {
                if (err.message) showError(err.message);
                else showCard(a);
            } else {
                showError(labels.lError);
            }
        } finally {
            if (mine === seq) delete stage.dataset.loading;
        }
    };
    const open = (link) => {
        restoreTo = link;
        list = groupOf(link);
        index = Math.max(0, list.findIndex((a) => a.getAttribute('href') === link.getAttribute('href')));
        overlay.classList.add('show');
        overlay.setAttribute('aria-hidden', 'false');
        closeBtn.focus();
        render();
    };
    const close = () => {
        if (!isOpen()) return;
        overlay.classList.remove('show');
        overlay.setAttribute('aria-hidden', 'true');
        reset();
        const back = restoreTo;
        restoreTo = null;
        if (back && back.isConnected) back.focus();
    };
    const step = (delta) => {
        const next = index + delta;
        if (next < 0 || next >= list.length) return;
        index = next;
        render();
    };
    // Word · PowerPoint 는 샌드박스 iframe 안에서 그려 그 안의 키가 이 문서에 오지 않는다 — 미리보기가 넘겨준다.
    function onKey(key) {
        if (!isOpen()) return;
        if (key === 'Escape') close();
        else if (key === 'ArrowLeft') step(-1);
        else if (key === 'ArrowRight') step(1);
    }
    const trap = (event) => {
        const items = [...overlay.querySelectorAll('a[href]:not([hidden]), button:not([disabled]):not([hidden])')];
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (!overlay.contains(document.activeElement)) {
            event.preventDefault();
            (event.shiftKey ? last : first).focus();
        } else if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    document.addEventListener('click', (event) => {
        const link = event.target.closest?.('a[data-file-view]');
        if (!link || overlay.contains(link)) return;
        // 새 탭 · 새 창 · 저장으로 여는 클릭은 브라우저에 맡긴다.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        open(link);
    });
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay) close();
    });
    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', () => step(-1));
    nextBtn.addEventListener('click', () => step(1));
    // 창의 capture 단계: 열린 동안에는 뒤에 있는 패널 · 모달의 Esc · 단축키 · 포커스 가두기보다 먼저 받고
    // 더 흘려보내지 않는다 (보기 창을 닫는 Esc 가 결재 문서 모달까지 닫지 않게).
    window.addEventListener('keydown', (event) => {
        if (!isOpen()) return;
        event.stopPropagation();
        if (event.key === 'Tab') {
            trap(event);
        } else if (event.key === 'Escape') {
            event.preventDefault();
            close();
        } else if (event.key === 'ArrowLeft' && list.length > 1) {
            event.preventDefault();
            step(-1);
        } else if (event.key === 'ArrowRight' && list.length > 1) {
            event.preventDefault();
            step(1);
        }
    }, { capture: true });
    // 뒤로 가기는 창부터 닫는다 (창은 body 수준이라 SPA 교체에 남는다). 배경 새로고침(calendar:navigated)에는
    // 닫지 않는다 — 실시간 갱신이 보던 첨부를 닫으면 안 된다.
    window.addEventListener('popstate', close);
}
